import { Info, MapPin } from "lucide-mui"
import React from "react"

import i18n from "@/i18n"
import { isVenueOpen, getTodayHours } from "@/utils/opening_hours"

import type { VenueHeaderProps } from "./types"

const STYLES = {
  container: "mb-6",
  row: "flex items-start justify-between gap-4",
  content: "min-w-0 flex-1",
  tagsContainer: "no-scrollbar -mx-4 flex gap-[6px] overflow-x-auto whitespace-nowrap px-4",
  tag: "shrink-0 rounded-[2px] bg-[#f4f4f4] px-1 py-[3px] text-[10px] leading-[8px] text-[#2d3f59]/60",
  title: "mb-0 mt-4 text-[22px] font-medium leading-[27px] text-[#111827]",
  addressContainer: "mt-[6px] flex flex-wrap items-center gap-x-2 gap-y-1",
  mapPinIcon: "h-[12px] w-[12px] shrink-0 text-[#303239] opacity-50",
  addressText: "text-[12px] leading-4 text-[#303239] opacity-50",
  mapLink:
    "cursor-pointer border-0 bg-transparent p-0 text-[12px] font-medium leading-4 text-[#2596fa] no-underline hover:underline",
  statusContainer: "mt-2 flex items-center gap-2",
  statusBadge: "inline-flex items-center rounded-[2px] px-1 py-[2px] text-[10px] font-medium leading-[10px] text-white",
  statusHours: "text-[10px] leading-[10px] text-[#0b0d0f]",
  buttonsRow: "mt-6 flex items-center gap-4",
  infoButton:
    "flex h-[34px] w-[55px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] border-0 bg-[#f1f1f0]",
  infoIcon: "h-4 w-4 text-[#201f1f]",
  bookButton:
    "h-[34px] flex-1 cursor-pointer rounded-[6px] border-0 bg-[#2596fa] text-[12px] font-medium leading-none text-white",
} as const

const VenueHeaderMobile: React.FC<VenueHeaderProps> = ({
  name,
  address,
  zipcode,
  city,
  openingHours,
  timezone,
  tags,
  onBookClick,
  onInfoClick,
  onShowMapClick,
  bookButtonRef,
}) => {
  const isOpen = isVenueOpen(openingHours, timezone)
  const todayHours = getTodayHours(openingHours, timezone)

  const venueTypeTags = tags?.filter((t) => t.category === "venue_type") || []
  const cityLine = [zipcode, city].filter(Boolean).join(" ")
  const locationParts = [address, cityLine].filter(Boolean)
  const locationText = locationParts.join(", ")
  const canShowMap = Boolean(address && city)
  const mapQuery = [name, address, zipcode, city].filter(Boolean).join(", ")

  return (
    <div className={STYLES.container} data-testid="public-venue-header-container">
      <div className={STYLES.row}>
        <div className={STYLES.content}>
          {venueTypeTags.length > 0 && (
            <div className={STYLES.tagsContainer} data-testid="public-venue-header-tags">
              {venueTypeTags.map((tag) => (
                <span key={tag.value} className={STYLES.tag} data-testid={`public-venue-header-tag-${tag.value}`}>
                  {tag.label}
                </span>
              ))}
            </div>
          )}

          <h1 className={STYLES.title} data-testid="public-venue-header-name">
            {name}
          </h1>

          {!!locationText && (
            <div className={STYLES.addressContainer} data-testid="public-venue-header-address">
              <MapPin className={STYLES.mapPinIcon} />
              <span className={STYLES.addressText}>{locationText}</span>
              {!!canShowMap && (
                <>
                  <span className={STYLES.addressText}>-</span>
                  {onShowMapClick ? (
                    <button
                      type="button"
                      onClick={onShowMapClick}
                      className={STYLES.mapLink}
                      data-testid="public-venue-header-map-link"
                    >
                      {i18n.t("app.public.venue.show_map")}
                    </button>
                  ) : (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={STYLES.mapLink}
                      data-testid="public-venue-header-map-link"
                    >
                      {i18n.t("app.public.venue.show_map")}
                    </a>
                  )}
                </>
              )}
            </div>
          )}

          <div className={STYLES.statusContainer} data-testid="public-venue-header-status">
            <span
              className={`${STYLES.statusBadge} ${isOpen ? "bg-[#5fc178]" : "bg-[#dc2626]"}`}
              data-testid="public-venue-header-open-status"
            >
              {isOpen ? i18n.t("app.public.venue.status.open_now") : i18n.t("app.public.venue.status.closed")}
            </span>
            {!!isOpen && !!todayHours?.endTime && (
              <span className={STYLES.statusHours} data-testid="public-venue-header-hours">
                {i18n.t("app.public.venue.status.closes_at", { time: todayHours.endTime })}
              </span>
            )}
          </div>

          {!!onBookClick && (
            <div className={STYLES.buttonsRow} data-testid="public-venue-header-actions">
              {!!onInfoClick && (
                <button
                  type="button"
                  onClick={onInfoClick}
                  className={STYLES.infoButton}
                  aria-label={i18n.t("app.public.venue.venue_info.title")}
                  data-testid="public-venue-header-info-button"
                >
                  <Info className={STYLES.infoIcon} />
                </button>
              )}
              <button
                type="button"
                ref={bookButtonRef}
                onClick={onBookClick}
                className={STYLES.bookButton}
                data-testid="public-venue-header-book-button"
              >
                {i18n.t("app.public.venue.book_table")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VenueHeaderMobile
